import React, { useState, useEffect, useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import Select from 'react-select';
import _ from 'lodash';
import { Card, CardHeader, CardBody, TabPane } from 'reactstrap';
import PieChart from 'components/PieChart';
import PageTabs from 'components/PageTabs';
import TightTable from 'components/TightTable';
import TightChart from 'components/TightChart';
import TimePeriod, { formatDates } from 'components/TimePeriod';
import LineChart from 'components/LineChart';
import { AutoColumns } from 'components/Columns';
import { useArrayState } from 'components/CustomHooks';
import { toDeci, toPerc, formatCurrency, toPercTable, toDeciTable } from 'helpers/formatter';
import { colors } from 'helpers/chart';
import { optionizeValue, optionizeAll, getValue, getDate } from 'helpers/select';
import { toAggregatedDataFrame, sortDataFrameByIndex, sortDataFrameByColumns } from 'helpers/data-frame';
import { formatYearMonthToMonthYear } from 'helpers/date';

function toDataFrame(input) {
  const entities = Object.keys(input.Symbol);
  const columns = Object.keys(input);

  const data = entities.map((entity) =>
    columns.map((column) => {
      const value = input[column][entity];
      // Convert percentage strings in the Weight column to decimals
      if (column === 'Weight' && typeof value === 'string' && value.endsWith('%')) {
        return parseFloat(value.replace('%', '')) / 100;
      }
      return value;
    }),
  );

  return {
    index: entities,
    columns,
    data,
    index_names: ['Entity'],
    column_names: ['Properties'],
  };
}

function Holdings({ data, params, notify, session }) {
  const [detailsDF, setDetailsDF] = useState(null);
  const [pieChartData, setPieChartData] = useState(null);
  const [typeOptions, setTypeOptions, selectedTypeOption, setSelectedTypeOption] = useArrayState({
    initialItems: [],
  });

  useEffect(() => {
    if (!data) return;

    const _detailsDF = toDataFrame(data);
    setDetailsDF(_detailsDF);

    const allowedColumns = _detailsDF.columns.filter((v) => !['Symbol', 'Weight'].includes(v));
    const _detailsTypeOptions = optionizeAll(['Holdings', ...allowedColumns]);
    setTypeOptions(_detailsTypeOptions);
    if (_detailsTypeOptions.length > 0) {
      setSelectedTypeOption(_detailsTypeOptions[0]);
    }

    const weightIndex = _detailsDF.columns.findIndex((col) => col === 'Weight');
    const weights = _detailsDF.data.map((row) => row[weightIndex]);
    const _pieChartData = {
      Holdings: toAggregatedDataFrame('Holdings', _detailsDF.index, weights),
    };

    for (let i = 0; i < allowedColumns.length; i++) {
      const column = allowedColumns[i];
      const columnIndex = _detailsDF.columns.findIndex((col) => col === column);
      const columnValues = _detailsDF.data.map((row) => row[columnIndex]);
      const aggregatedData = toAggregatedDataFrame(column, columnValues, weights);
      _pieChartData[column] = aggregatedData;
    }

    setPieChartData(_pieChartData);
  }, [data]);

  const { tableData } = useMemo(() => {
    if (!detailsDF || !selectedTypeOption) return {};

    const keyIndex = detailsDF.columns.findIndex((col) => col === selectedTypeOption.value);
    const weightIndex = detailsDF.columns.findIndex((col) => col === 'Weight');
    const duplicatedKeys = [];
    const weights = [];

    detailsDF.data.forEach((row) => {
      duplicatedKeys.push(row[keyIndex]);
      weights.push(row[weightIndex]);
    });

    const isHoldings = selectedTypeOption.value === 'Holdings';

    const chartData = sortDataFrameByIndex(
      toAggregatedDataFrame(selectedTypeOption.value, isHoldings ? detailsDF.index : duplicatedKeys, weights),
    );

    const tableData = sortDataFrameByIndex(chartData);

    return { chartData, tableData };
  }, [detailsDF, selectedTypeOption]);

  if (!detailsDF || !tableData) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader tag="h4" className="mt-0 text-center">
          Details
        </CardHeader>
        <CardBody>
          <TightTable
            suffix="summary_details"
            data={detailsDF}
            indexName="Holdings"
            cellFormatter={(value, row, col) => {
              return ['Weight'].includes(col) ? toPercTable(value) : value;
            }}
            useSort
          />
        </CardBody>
      </Card>
      <div className="tw-grid tw-grid-cols-12 tw-gap-4">
        <div className="tw-col-start-5 tw-col-span-4">
          <div className="tw-flex tw-gap-2">
            <div className="tw-min-w-[200px]">
              <div className="tw-text-left tw-font-bold">SELECT</div>
              <Select
                className="react-select"
                name="duration"
                value={selectedTypeOption}
                onChange={setSelectedTypeOption}
                options={typeOptions}
              />
            </div>
          </div>

          {selectedTypeOption && pieChartData && (
            <>
              <Card>
                <CardHeader tag="h4" className="mt-0 text-center">
                  {selectedTypeOption.label}
                </CardHeader>
                <CardBody>
                  <PieChart
                    data={pieChartData[selectedTypeOption.value]}
                    base="rows"
                    field="Weight"
                    dataType="percentage"
                    containerStyle={{ maxWidth: '400px', maxHeight: '400px' }}
                    options={{
                      plugins: {
                        legend: {
                          display: pieChartData[selectedTypeOption.value].index.length <= 6,
                          position: 'bottom',
                        },
                      },
                      layout: {
                        padding: {
                          top: 0,
                          bottom: 0,
                          left: 0,
                          right: 0,
                        },
                      },
                    }}
                    // containerStyle={{ maxWidth: '500px', maxHeight: '500px' }}
                  />
                </CardBody>
              </Card>
              <Card>
                <CardHeader tag="h4" className="mt-0 text-center">
                  &nbsp;
                </CardHeader>
                <CardBody>
                  <TightTable
                    suffix="pba-filtered-details"
                    data={tableData}
                    indexName={selectedTypeOption.label}
                    cellFormatter={(value, row, col) => {
                      if (col === 'Weight') return toPercTable(value);
                      return value;
                    }}
                    indexWidth="300px"
                    compact
                    useSort
                  />
                </CardBody>
              </Card>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default Holdings;
